import { CheckCircle, HelpCircle, InfoIcon, SparklesIcon } from "lucide-react";
import { useState } from "react";

interface Props {
    content: string;
    type: "warning" | "success" | string;
    position?: "top" | "bottom" | "left" | "right";
    onClose: () => void;
    className?: string;
}

export default function ResumeAnalyzeTooltip({ position = "bottom", ...props }: Props) {
    const [showHoverTip, setShowHoverTip] = useState(false);

    // Updated color classes with yellow styling for warning and info types
    // Removed opacity modifiers to ensure 100% opacity
    const bgColorClass =
        props.type === "warning"
            ? "bg-yellow-100 dark:bg-yellow-900"
            : props.type === "success"
                ? "bg-green-100 dark:bg-green-900"
                : "bg-yellow-100 dark:bg-yellow-900";

    const borderColorClass =
        props.type === "warning"
            ? "border-yellow-400 dark:border-yellow-600"
            : props.type === "success"
                ? "border-green-400 dark:border-green-600"
                : "border-yellow-400 dark:border-yellow-600";

    const textColorClass =
        props.type === "warning"
            ? "text-yellow-600 dark:text-yellow-300"
            : props.type === "success"
                ? "text-green-600 dark:text-green-300"
                : "text-yellow-600 dark:text-yellow-300";

    const IconComponent =
        props.type === "warning"
            ? HelpCircle
            : props.type === "success"
                ? CheckCircle
                : InfoIcon;

    // Updated: Sparkle icon now matches the tooltip type but in a darker shade
    const sparkleColorClass =
        props.type === "warning" || props.type === "info"
            ? "text-yellow-600 dark:text-yellow-400"
            : "text-green-600 dark:text-green-400";

    // Updated: Tooltip background color now using amber-50 for warning/info and green-50 for success
    const tooltipBgColorClass =
        props.type === "warning" || props.type === "info"
            ? "bg-amber-50 dark:bg-yellow-900"
            : "bg-green-50 dark:bg-green-900";

    const tooltipBorderColorClass =
        props.type === "warning" || props.type === "info"
            ? "border-yellow-300 dark:border-yellow-700"
            : "border-green-300 dark:border-green-700";

    const tooltipTextColorClass =
        props.type === "warning" || props.type === "info"
            ? "text-yellow-800 dark:text-yellow-200"
            : "text-green-800 dark:text-green-200";

    // Updated: Arrow color now matches tooltip background with amber-50/green-50
    const arrowBgColorClass =
        props.type === "warning" || props.type === "info"
            ? "bg-amber-50 dark:bg-yellow-900"
            : "bg-green-50 dark:bg-green-900";

    const arrowBorderColorClass =
        props.type === "warning" || props.type === "info"
            ? "border-yellow-300 dark:border-yellow-700"
            : "border-green-300 dark:border-green-700";

    return (
        <div className={`inline-flex items-center gap-2 ${props.className}`}>
            <div
                className={`relative flex items-center justify-center p-1.5 rounded-full border ${bgColorClass} ${borderColorClass} cursor-help transition-all duration-150`}
                onMouseEnter={() => setShowHoverTip(true)}
                onMouseLeave={() => setShowHoverTip(false)}
            >
                <IconComponent className={`h-3.5 w-3.5 ${textColorClass}`} />
                {showHoverTip && (
                    <div
                        className={`absolute z-10 min-w-[280px] max-w-[320px] whitespace-normal px-4 py-2 rounded-md shadow-md text-sm font-medium ${tooltipBgColorClass} ${tooltipTextColorClass} border ${tooltipBorderColorClass} ${position === "top"
                            ? "bottom-full left-1/2 transform -translate-x-1/2 mb-2"
                            : position === "bottom"
                                ? "top-full left-1/2 transform -translate-x-1/2 mt-2"
                                : position === "left"
                                    ? "right-full top-1/2 transform -translate-y-1/2 mr-2"
                                    : "bottom-1/2 left-full transform translate-y-1/2 ml-2" // default right
                            }`}
                    >
                        <div className="flex items-start gap-2">
                            <SparklesIcon
                                className={`h-5 w-5 ${sparkleColorClass} flex-shrink-0 mt-0.5`}
                            />
                            <p className="text-sm">{props.content}</p>
                        </div>
                        <div
                            className={`absolute w-2 h-2 rotate-45 ${arrowBgColorClass} border ${arrowBorderColorClass} ${position === "top"
                                ? "top-full left-1/2 transform -translate-x-1/2 -mt-1 border-b border-r"
                                : position === "bottom"
                                    ? "bottom-full left-1/2 transform -translate-x-1/2 -mb-1 border-t border-l"
                                    : position === "left"
                                        ? "left-full top-1/2 transform -translate-y-1/2 -ml-1 border-t border-r"
                                        : "top-1/2 -left-1 transform -translate-y-1/2 border-l border-b" // default right
                                }`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
